import React, { useEffect, useState } from "react";

import { Icon } from "@iconify/react";
import { CircularProgress, Tooltip } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";

import "../../css/documentView.css";
import { performDownload } from "../DocumentManagementView/DocumentGrid.utils";

import { usePostSummarize } from "@/api/apiPostSummarize";
import { usePostDownloadBlobs } from "@/api/usePostDownloadBlobs";
import { usePostPreviewBlobs } from "@/api/usePostPreviewBlobs";
import { DetailView } from "@/components/DocumentView/DetailView";
import { DisplayNotAvailable } from "@/components/DocumentView/DisplayNotAvailable";
import { PDFViewer } from "@/components/DocumentView/PDFViewer";
import { SummaryView } from "@/components/DocumentView/SummaryView";
import { Alerts } from "@/components/shared/Alerts/Alerts";
import { DeleteModal } from "@/components/shared/Modal/DeleteModal";
import { EditModal } from "@/components/shared/Modal/EditModal";
import { OverrideModal } from "@/components/shared/Modal/OverrideModal";
import { usePermissions } from "@/context/PermissionsContext";
import { usePollingSummarizeStatus } from "@/hooks/usePollingSummarizeStatus";
import { AlertColor, SUMMARY_STATUS_FAILED, SUMMARY_STATUS_SUCCESS } from "@/util/constants";
import { DocumentData } from "@/util/interfaces/DocumentData";
import { Permission } from "@/utils/policies";

export const DocumentView: React.FC = () => {
  const { hasPermission } = usePermissions();
  const { mutateAsync: downloadBlobs } = usePostDownloadBlobs();
  const { mutateAsync: previewBlobs } = usePostPreviewBlobs();
  const navigate = useNavigate();

  const [downloadLoading, setDownloadLoading] = useState<boolean>(false);
  const [params] = useSearchParams();
  const [alertColor, setAlertColor] = useState<AlertColor>(AlertColor.Info);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [isExpanded, setIsExpanded] = useState(true);
  const [downloadedBlob, setDownloadedBlob] = useState<Blob | null>(null);
  const [showModalEdit, setShowModalEdit] = useState<boolean>(false);
  const [showModalDelete, setShowModalDelete] = useState<boolean>(false);
  const [showModalOverride, setShowModalOverride] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState("details");

  const [documentDetails, setDocumentDetails] = useState<DocumentData>(
    JSON.parse(
      localStorage.getItem(`doc-${params.get("id")}`) ??
        JSON.stringify({
          id: 0,
          documentName: "",
          fileType: "",
          url: "",
          category: "",
          uploaded: "",
          uploadedBy: "",
          metadata: "",
          previewUrl: "",
        })
    )
  );
  const { mutateAsync: summarize } = usePostSummarize();
  const [summaryStatus, setSummaryStatus] = useState<string>("PENDING");
  const [summary, setSummary] = useState("");
  const {
    currentData,
    status,
    summary: summaryResponse,
  } = usePollingSummarizeStatus({
    blob_name: documentDetails?.file_name,
    enabled:
      summaryStatus !== SUMMARY_STATUS_SUCCESS &&
      summaryStatus !== SUMMARY_STATUS_FAILED &&
      hasPermission(Permission.VIEWER_SUMMARY),
  });
  useEffect(() => {
    if (currentData) {
      if (currentData.httpStatus === 404) {
        startSummarization(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentData]);
  useEffect(() => {
    setSummaryStatus(status as string);
    if (summaryResponse) {
      setSummary(summaryResponse);
    }
  }, [status, summaryResponse]);

  const getDisplayedScreen = (fileType: string) => {
    switch (fileType) {
      case "xls":
      case "xlsx":
      case "csv":
        return <DisplayNotAvailable />;
      default:
        return <PDFViewer blob={downloadedBlob} />;
    }
  };

  const startSummarization = async (updateState: boolean) => {
    try {
      let documentName = documentDetails.file_name;
      const response = await summarize({ blob_name: documentName });
      if (response.status != 202) {
        throw new Error("Failed to start summarization");
      }
      if (updateState) {
        setSummaryStatus(response.data.status);
      }
    } catch {
      return false;
    }
  };
  const handleDownloadFile = async () => {
    setDownloadLoading(true);
    try {
      const { data } = await downloadBlobs({
        blob_names: [{ name: documentDetails.documentName, uuid: documentDetails.uuid }],
        folder_path: [],
      });
      performDownload(data, documentDetails.file_extension, documentDetails.documentName);
    } catch {
      setDownloadLoading(false);
      return false;
    }
    setDownloadLoading(false);
  };

  const renderTab = (selectedTab: string) => {
    switch (selectedTab) {
      case "details":
        return <DetailView documentDetails={documentDetails} />;
      case "summary":
        return (
          <SummaryView
            loading={
              summaryStatus !== SUMMARY_STATUS_SUCCESS && summaryStatus !== SUMMARY_STATUS_FAILED
            }
            summary={summary}
          />
        );
      default:
        break;
    }
  };
  const downloadBlob = async () => {
    const response = await previewBlobs({
      name: documentDetails.documentName,
      uuid: documentDetails.uuid,
    });
    setDownloadedBlob(response);
  };
  useEffect(() => {
    if (!["xls", "xlsx", "csv"].includes(documentDetails.fileType)) {
      downloadBlob();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const editDocumentDetails = (updatedFile: DocumentData, downloadAgain?: boolean) => {
    const editedDocument = {
      ...documentDetails,
      metadata: updatedFile.metadata,
      category: updatedFile.category,
      uploaded: updatedFile.uploaded,
      uploadedBy: updatedFile.uploadedBy,
    };
    setDocumentDetails(editedDocument);
    localStorage.setItem(`doc-${params.get("id")}`, JSON.stringify(editedDocument));
    if (downloadAgain) {
      downloadBlob();
    }
  };
  return (
    <>
      <div
        className="flex flow-column flex-1"
        data-testid="doc_viewer"
        style={{ backgroundColor: "#F8F8F8" }}
      >
        <div className="document-view-header">
          <span className="page-title">Document Viewer</span>
        </div>
        <div className="flex space-between document-view-title-section">
          <Tooltip title={documentDetails ? documentDetails.documentName : null} arrow>
            <span className="title text-overflow-ellipsis" style={{ maxWidth: "70vw" }}>
              {documentDetails.documentName}
            </span>
          </Tooltip>
          <div className="flex" style={{ gap: "8px" }}>
            {hasPermission(Permission.VIEWER_EDIT) && (
              <Tooltip arrow title="Edit file">
                <button
                  className="document-view-title-section-btn-icons"
                  data-testid="edit-button"
                  onClick={() => setShowModalEdit(true)}
                >
                  <Icon
                    icon="mdi:file-edit-outline"
                    className="document-view-title-section-icons"
                  />
                </button>
              </Tooltip>
            )}
            {hasPermission(Permission.VIEWER_DOWNLOAD) && (
              <Tooltip arrow title="Download file">
                <button
                  className="document-view-title-section-btn-icons"
                  data-testid="download-button"
                  onClick={handleDownloadFile}
                  disabled={downloadLoading}
                >
                  {downloadLoading ? (
                    <CircularProgress size={15} />
                  ) : (
                    <Icon icon="lucide:download" className="document-view-title-section-icons" />
                  )}
                </button>
              </Tooltip>
            )}
            {hasPermission(Permission.VIEWER_UPDATE) && (
              <Tooltip arrow title="Update file">
                <button
                  className="document-view-title-section-btn-icons"
                  data-testid="override-button"
                  onClick={() => setShowModalOverride(true)}
                >
                  <Icon icon="eva:file-add-fill" className="document-view-title-section-icons" />
                </button>
              </Tooltip>
            )}
            {hasPermission(Permission.VIEWER_DELETE) && (
              <Tooltip arrow title="Delete file">
                <button
                  className="document-view-title-section-btn-icons"
                  data-testid="delete-button"
                  onClick={() => setShowModalDelete(true)}
                >
                  <Icon
                    icon="material-symbols:delete-outline"
                    className="document-view-title-section-icons"
                  />
                </button>
              </Tooltip>
            )}
          </div>
        </div>
        <div className={`document-view ${isExpanded === false ? "collapsed" : ""}`}>
          <div className="pdf">
            <div
              style={{
                position: "absolute",
                width: "100%",
                maxWidth: "100%",
              }}
            >
              {getDisplayedScreen(documentDetails.fileType)}
            </div>
          </div>
          <div className="details">
            {isExpanded && (
              <div className="document-view-doc-details flex flow-column">
                <div className="document-view-doc-details-header-section flex">
                  <button
                    className="document-view-doc-details-collapse-btn cursor-pointer"
                    onClick={() => setIsExpanded(!isExpanded)}
                    data-testid="collapse-button"
                  >
                    <Icon
                      icon="ep:arrow-right"
                      className="document-view-doc-details-collapse-btn-icon"
                    />
                  </button>
                  <div className="document-view-doc-details-title flex flex-1">
                    <span>Information</span>
                  </div>
                </div>
                <div className="document-view-doc-details-body flex flow-column">
                  <div className="flex" style={{ gap: "19px" }}>
                    <div className="flex flow-column" style={{ gap: "8px" }}>
                      <button
                        onClick={() => setSelectedTab("details")}
                        className="document-view-doc-details-body-header-title"
                        style={{
                          color: selectedTab === "details" ? "#4d4d4d" : "#b8b8b8",
                          fontWeight: selectedTab === "details" ? "400" : "200",
                          borderBottom:
                            selectedTab === "details" ? "2px solid green" : "1px solid grey",
                        }}
                      >
                        Details
                      </button>
                    </div>
                    <div className="flex flow-column" style={{ gap: "8px" }}>
                      {hasPermission(Permission.VIEWER_SUMMARY) && (
                        <button
                          onClick={() => setSelectedTab("summary")}
                          className="document-view-doc-details-body-header-title"
                          style={{
                            color: selectedTab === "summary" ? "#4d4d4d" : "#b8b8b8",
                            fontWeight: selectedTab === "summary" ? "400" : "200",
                            borderBottom:
                              selectedTab === "summary" ? "2px solid green" : "1px solid grey",
                            cursor:
                              documentDetails.fileType === "csv" ||
                              documentDetails.fileType === "xls" ||
                              documentDetails.fileType === "xlsx"
                                ? "not-allowed"
                                : "pointer",
                          }}
                          disabled={
                            documentDetails.fileType === "csv" ||
                            documentDetails.fileType === "xls" ||
                            documentDetails.fileType === "xlsx"
                          }
                        >
                          Summary
                        </button>
                      )}
                    </div>
                    <div
                      style={{
                        display:
                          documentDetails.fileType === "csv" ||
                          documentDetails.fileType === "xls" ||
                          documentDetails.fileType === "xlsx"
                            ? "flex"
                            : "none",
                      }}
                      className="coming_soon_alert"
                    >
                      Coming soon
                    </div>
                  </div>
                  {renderTab(selectedTab)}
                </div>
              </div>
            )}
            {!isExpanded && (
              <button
                className="document-view-doc-details-expand-btn cursor-pointer"
                onClick={() => setIsExpanded(!isExpanded)}
              >
                <Icon icon="ep:arrow-left" className="document-view-doc-details-expand-btn-icon" />
              </button>
            )}
          </div>
        </div>
      </div>
      {showModalEdit && (
        <EditModal
          callbackClose={(updatedFile: DocumentData | null) => {
            setShowModalEdit(false);
            if (updatedFile) {
              setAlertMessage("You have successfully edited the file.");
              setAlertColor(AlertColor.Success);
              editDocumentDetails(updatedFile);
            }
          }}
          file={documentDetails}
        />
      )}
      {showModalOverride && (
        <OverrideModal
          callbackClose={(fileEdited: DocumentData | null) => {
            setShowModalOverride(false);
            if (fileEdited) {
              setAlertMessage("You have successfully overrided selected file.");
              setAlertColor(AlertColor.Success);
              setSummaryStatus("PENDING");
              setSummary("");
              editDocumentDetails(fileEdited, true);
            }
          }}
          file={documentDetails}
        />
      )}

      {showModalDelete && (
        <DeleteModal
          callbackClose={(update: boolean) => {
            setShowModalDelete(false);
            if (update) {
              localStorage.removeItem(`doc-${params.get("id")}`);
              navigate("/");
            }
          }}
          documents={[documentDetails]}
        />
      )}

      <Alerts
        alertOpen={alertMessage !== ""}
        color={alertColor}
        message={alertMessage}
        handleAlertClose={() => setAlertMessage("")}
      />
    </>
  );
};
